import type {
  MediaToolsModelConfig,
  MediaToolsModelType,
  MediaToolsModelToolConfig,
  ModelConfig,
  FlowNodeData,
  ModelToolParameterItem,
} from '$/domains/workFlowV3/type';
import useTeamFlowStore from '$/store/useTeamFlowStore';
import { useLanguageStore } from '$/store/languageStore';
import type { LanguageKeyMap } from '$/store/languageStore/smartcatTranslation';
import { TVFlowNodeNextTypeEnum } from '../config/node.config';
import type { ImageNodeGenerationConfig } from '../nodes/ImageNode/types';
import type {
  ConfigPanelOption,
  ConfigPanelRemoteParameter,
  ConfigPanelRemoteParameters,
} from '../FlowContent/components/NodeBottomPanel/ConfigPanel/config';

type ParameterValue = string | number | boolean;
type MediaType = Extract<MediaToolsModelType, 'image' | 'video' | 'audio' | 'text' | 'script'>;
export type GenerationConfig = VideoGenerationConfig | ImageNodeGenerationConfig;
type ModelParameterConfigs = Record<string, ModelToolParameterItem | ConfigPanelRemoteParameter | undefined>;

export interface GetNormalizedParameterConfigsOptions {
  includeKeys?: string[];
  excludeKeys?: string[];
  keyOrder?: string[];
  optionFallbacks?: Record<string, ConfigPanelOption[]>;
  nameFallbacks?: Record<string, string>;
}

export type GetNodeConfigPanelParametersOptions = Omit<GetNormalizedParameterConfigsOptions, 'includeKeys' | 'excludeKeys'>;
export type GetNodeConfigPanelParametersFilter = string[] | {
  type: 'pick' | 'omit';
  keys: string[];
};

export interface ImageModelMenuOption {
  key: string;
  value: string;
  label: string;
  icon?: string;
  duration: string;
  model_desc?: string;
  badgeLabel?: string;
  model_tag?: string;
}

export interface VideoGenerationConfig {
  model_id: string;
  model_name: string;
  api_name: string;
  node_model_id: string;
  name: string;
  parameters: Record<string, ParameterValue>;
}

const DEFAULT_DURATION = '50s';

const NODE_TYPE_TO_MEDIA_TYPE_MAP: Partial<Record<TVFlowNodeNextTypeEnum, MediaType>> = {
  [TVFlowNodeNextTypeEnum.IMAGE_NODE]: 'image',
  [TVFlowNodeNextTypeEnum.VIDEO_NODE]: 'video',
  [TVFlowNodeNextTypeEnum.AUDIO_NODE]: 'audio',
  [TVFlowNodeNextTypeEnum.TEXT_NODE]: 'text',
  [TVFlowNodeNextTypeEnum.SCRIPT_NODE]: 'script',
};

const PARAMETER_NAME_FALLBACK_TRANSLATION_KEYS: Partial<Record<string, keyof LanguageKeyMap>> = {
  ratio: 'ratio_opacity',
  resolution: 'video_quality',
  duration: 'length',
  video_duration: 'length',
  sound: 'generate_audio',
  generate_audio: 'generate_audio',
};

/**
 * 从语言包读取模型配置面板文案，缺失时回退到传入的默认值。
 */
const getModelConfigI18nText = (
  key: keyof LanguageKeyMap,
  fallback = '',
): string => {
  const { languageStore } = useLanguageStore.getState();
  return languageStore.smartcatTranslationText((v) => v[key]) || fallback;
};

/**
 * 将画布节点类型映射为模型配置中的媒体类型。
 */
const resolveMediaTypeByNodeType = (
  nodeType: TVFlowNodeNextTypeEnum,
): MediaType | undefined => {
  return NODE_TYPE_TO_MEDIA_TYPE_MAP[nodeType];
};

/**
 * 从 TeamFlow Store 读取当前全局模型配置。
 */
function getTeamFlowModelConfig(): ModelConfig | undefined {
  const { modelConfig } = useTeamFlowStore.getState();
  return modelConfig;
}

/**
 * 从全局模型配置中按媒体类型取出模型列表，非数组时返回空数组。
 */
const getModelsByType = (
  type: MediaType,
): MediaToolsModelConfig[] => {
  const models = getTeamFlowModelConfig()?.[type];
  return Array.isArray(models) ? models : [];
};

/**
 * 在模型列表中按 model_id 查找模型，未命中时回退到列表首项。
 */
function getModelByIdFromModels(
  models: MediaToolsModelConfig[] | undefined,
  modelId?: string,
): MediaToolsModelConfig | undefined {
  const modelList = Array.isArray(models) ? models : [];
  return modelList.find((model) => model.model_id === modelId) ?? modelList[0];
}

/**
 * 按媒体类型与 model_id 从全局配置中查找模型。
 */
export function getModelById(
  type: MediaType,
  modelId?: string,
): MediaToolsModelConfig | undefined {
  return getModelByIdFromModels(getModelsByType(type), modelId);
}

/**
 * 按节点保存的 api_name 优先回查工具，避免多个工具共用 node_model_id 时丢失 quickStart 指定的 apiName。
 */
const getToolByIdFromModels = (
  model: MediaToolsModelConfig | undefined,
  models: MediaToolsModelConfig[] | undefined,
  nodeModelId?: string,
  apiName?: string,
): MediaToolsModelToolConfig | undefined => {
  const toolList = model?.tool_list?.length
    ? model.tool_list
    : models?.find((item) => item.tool_list?.length)?.tool_list;
  if (!toolList?.length) return undefined;
  return toolList.find((tool) => (
    apiName
    && nodeModelId
    && tool.api_name === apiName
    && tool.node_model_id === nodeModelId
  ))
    ?? toolList.find((tool) => apiName && tool.api_name === apiName)
    ?? toolList.find((tool) => nodeModelId && tool.node_model_id === nodeModelId)
    ?? toolList[0];
};

/**
 * 根据模型类型回查工具配置，优先保留节点当前 api_name。
 */
export function getToolById(
  model: MediaToolsModelConfig | undefined,
  type: MediaType,
  nodeModelId?: string,
  apiName?: string,
): MediaToolsModelToolConfig | undefined {
  return getToolByIdFromModels(model, getModelsByType(type), nodeModelId, apiName);
}

/**
 * 将模型与工具配置合并为节点可用的生成配置，并填充参数默认值。
 */
const buildGenerationConfig = (
  model: MediaToolsModelConfig | undefined,
  tool: MediaToolsModelToolConfig | undefined,
): VideoGenerationConfig | undefined => {
  if (!model || !tool) return undefined;

  return {
    model_id: model.model_id,
    model_name: model.model_name,
    api_name: tool.api_name,
    node_model_id: tool.node_model_id,
    name: tool.name,
    parameters: getDefaultParams(tool.parameters),
  };
};

/**
 * 将生成配置转换为节点 data 中的 mcpInfo 结构，可覆盖参数。
 */
export const buildMcpInfoFromGenerationConfig = (
  generationConfig: VideoGenerationConfig | ImageNodeGenerationConfig | undefined,
  parameters?: Record<string, ParameterValue | string[]>,
): FlowNodeData['mcpInfo'] | undefined => {
  if (!generationConfig) return undefined;

  return {
    api_name: generationConfig.api_name,
    model_id: generationConfig.model_id,
    node_model_id: generationConfig.node_model_id,
    parameters: parameters ?? generationConfig.parameters ?? {},
  };
};

/**
 * 根据指定媒体类型的模型列表，生成新建节点时的默认 mcpInfo 数据。
 */
export function getInitialNodeMcpData(
  models?: ModelConfig[MediaToolsModelType],
): Record<string, any> {
  const model = getModelByIdFromModels(models);
  const tool = getToolByIdFromModels(model, models);
  const generationConfig = buildGenerationConfig(model, tool);
  if (!generationConfig) return {};

  return {
    mcpInfo: buildMcpInfoFromGenerationConfig(generationConfig),
  };
}

/**
 * 获取指定节点类型的默认生成配置（取该类型下首个模型与工具）。
 */
export function getDefaultGenerationConfig(
  nodeType: TVFlowNodeNextTypeEnum,
): GenerationConfig | undefined {
  const mediaType = resolveMediaTypeByNodeType(nodeType);
  if (!mediaType) return undefined;

  const model = getModelById(mediaType);
  const tool = getToolById(model, mediaType);
  return buildGenerationConfig(model, tool);
}

/**
 * 按快速开始配置里的 model/api 精确解析默认生成配置，找不到时回退到该节点类型默认配置。
 */
export const getGenerationConfigByModelAndTool = (
  nodeType: TVFlowNodeNextTypeEnum,
  modelId?: string,
  apiName?: string,
  nodeModelId?: string,
): GenerationConfig | undefined => {
  const mediaType = resolveMediaTypeByNodeType(nodeType);
  if (!mediaType) return undefined;

  const models = getModelsByType(mediaType);
  const model = models.find((item) => item.model_id === modelId)
    ?? models.find((item) => item.tool_list?.some((tool) => {
      if (apiName && tool.api_name === apiName) return true;
      if (nodeModelId && tool.node_model_id === nodeModelId) return true;
      return false;
    }))
    ?? models[0];
  const toolList = model?.tool_list?.length
    ? model.tool_list
    : models.find((item) => item.tool_list?.length)?.tool_list;
  const matchedTool = toolList?.find((tool) => {
    return Boolean(
      apiName
      && nodeModelId
      && tool.api_name === apiName
      && tool.node_model_id === nodeModelId,
    );
  }) ?? toolList?.find((tool) => {
    if (apiName && tool.api_name === apiName) return true;
    if (!apiName && nodeModelId && tool.node_model_id === nodeModelId) return true;
    return false;
  }) ?? getToolByIdFromModels(model, models, nodeModelId, apiName);

  return buildGenerationConfig(model, matchedTool) ?? getDefaultGenerationConfig(nodeType);
};

/**
 * 将服务端模型配置归一化为下拉菜单展示数据，保留后端描述和 iconfont 供展示层直接渲染。
 */
export const getModelMenuOptions = (
  nodeType: TVFlowNodeNextTypeEnum,
): ImageModelMenuOption[] => {
  const mediaType = resolveMediaTypeByNodeType(nodeType);
  if (!mediaType) return [];

  const modelIdCount: Record<string, number> = {};
  return getModelsByType(mediaType).map((model) => {
    const currentCount = modelIdCount[model.model_id] ?? 0;
    modelIdCount[model.model_id] = currentCount + 1;
    return {
      key: `${model.model_id}-${currentCount}`,
      value: model.model_id,
      label: model.model_name,
      icon: model.model_iconfont,
      duration: DEFAULT_DURATION,
      model_desc: model.model_desc,
      // 透传服务端限时折扣标签，供模型下拉菜单展示
      model_tag: model.model_tag,
    };
  });
};

/**
 * 获取当前节点工具参数，按 generationConfig.api_name 精确回查工具配置。
 */
export const getCurrentToolParameters = (
  nodeType: TVFlowNodeNextTypeEnum,
  generationConfig?: GenerationConfig,
): Record<string, ModelToolParameterItem> => {
  const mediaType = resolveMediaTypeByNodeType(nodeType);
  if (!mediaType) return {};

  const model = getModelById(mediaType, generationConfig?.model_id);
  const tool = getToolById(
    model,
    mediaType,
    generationConfig?.node_model_id,
    generationConfig?.api_name,
  );
  return tool?.parameters ?? {};
};

/**
 * 按候选 key 列表从当前工具参数中查找首个存在的参数项。
 */
export const getCurrentToolParameterByKeys = (
  nodeType: TVFlowNodeNextTypeEnum,
  generationConfig: GenerationConfig | undefined,
  keys: string[],
): { key: string; parameter?: ModelToolParameterItem; } => {
  const parameters = getCurrentToolParameters(nodeType, generationConfig);
  const key = keys.find((item) => Boolean(parameters[item])) ?? keys[0];
  return {
    key,
    parameter: parameters[key],
  };
};

/**
 * 将参数配置条目按指定 key 顺序排序，未出现在顺序表中的 key 排在末尾。
 */
const getOrderedParameterEntries = (
  parameterConfigs: ModelParameterConfigs,
  keyOrder: string[] | undefined,
) => {
  if (!keyOrder?.length) return Object.entries(parameterConfigs);

  const orderIndex = (key: string) => {
    const index = keyOrder.indexOf(key);
    return index === -1 ? keyOrder.length : index;
  };

  return Object.entries(parameterConfigs)
    .sort(([a], [b]) => orderIndex(a) - orderIndex(b));
};

/**
 * 归一化配置面板选项，对自适应比例等特殊值做国际化文案处理。
 */
const normalizeConfigPanelOption = (
  parameter: ModelToolParameterItem | ConfigPanelRemoteParameter,
  option: ConfigPanelOption,
): ConfigPanelOption => {
  const value = String(option.value);
  const isAdaptiveRatio = parameter.type === 'ratio' && (value === 'adaptive' || value === 'auto');

  return {
    ...option,
    icon: option.icon || undefined,
    label: isAdaptiveRatio
      ? getModelConfigI18nText('self_fit', String(option.label ?? ''))
      : option.label,
  };
};

/**
 * 解析参数展示名称：优先自定义回退，其次语言包映射，最后按参数类型推断。
 */
const getParameterNameFallback = (
  key: string,
  parameter: ModelToolParameterItem | ConfigPanelRemoteParameter,
  nameFallbacks: Record<string, string>,
) => {
  if (nameFallbacks[key]) return nameFallbacks[key];

  const translationKey = PARAMETER_NAME_FALLBACK_TRANSLATION_KEYS[key];
  if (translationKey) {
    const text = getModelConfigI18nText(translationKey);
    if (text) return text;
  }

  if (parameter.type === 'slider') return getModelConfigI18nText('length');
  if (parameter.type === 'switch') return getModelConfigI18nText('generate_audio');
  return undefined;
};

/**
 * 将工具参数配置归一化为配置面板可用的远程参数结构，支持筛选、排序与选项回退。
 */
export const getNormalizedParameterConfigs = (
  parameterConfigs: ModelParameterConfigs | undefined,
  options: GetNormalizedParameterConfigsOptions = {},
): ConfigPanelRemoteParameters => {
  const includeKeySet = options.includeKeys?.length ? new Set(options.includeKeys) : undefined;
  const excludeKeySet = new Set(['prompt', ...(options.excludeKeys ?? [])]);
  const nameFallbacks = options.nameFallbacks ?? {};

  const orderedEntries = getOrderedParameterEntries(parameterConfigs ?? {}, options.keyOrder);

  return orderedEntries.reduce<ConfigPanelRemoteParameters>((acc, [key, parameter]) => {
    if (!parameter) return acc;
    if (includeKeySet && !includeKeySet.has(key) && (!parameter.type || !includeKeySet.has(parameter.type))) return acc;
    if (excludeKeySet.has(key)) return acc;
    if (parameter.type && excludeKeySet.has(parameter.type)) return acc;

    const fallbackOptions = options.optionFallbacks?.[key];
    const resolvedOptions = parameter.options?.length ? parameter.options : fallbackOptions;
    const normalizedParameter: ConfigPanelRemoteParameter = {
      ...parameter,
      name: parameter.name ?? getParameterNameFallback(key, parameter, nameFallbacks),
      type: parameter.type,
    };

    if (resolvedOptions?.length) {
      normalizedParameter.options = resolvedOptions.map((option) => (
        normalizeConfigPanelOption(parameter, option)
      ));
    }

    acc[key] = normalizedParameter;
    return acc;
  }, {});
};

/**
 * 将节点配置面板的参数过滤器解析为 includeKeys / excludeKeys / keyOrder 选项。
 */
const resolveNodeConfigPanelFilterOptions = (
  parameterFilter: GetNodeConfigPanelParametersFilter | undefined,
  keyOrder: string[] | undefined,
) => {
  if (!parameterFilter) return {};

  if (Array.isArray(parameterFilter)) {
    return parameterFilter.length
      ? {
        includeKeys: parameterFilter,
        keyOrder: keyOrder ?? parameterFilter,
      }
      : {};
  }

  if (!parameterFilter.keys.length) return {};

  if (parameterFilter.type === 'pick') {
    return {
      includeKeys: parameterFilter.keys,
      keyOrder: keyOrder ?? parameterFilter.keys,
    };
  }

  return {
    excludeKeys: parameterFilter.keys,
  };
};

/**
 * 获取节点底部配置面板所需的参数列表，结合当前生成配置与过滤规则。
 */
export const getNodeConfigPanelParameters = (
  nodeType: TVFlowNodeNextTypeEnum,
  generationConfig: GenerationConfig | undefined,
  parameterFilter?: GetNodeConfigPanelParametersFilter,
  options: GetNodeConfigPanelParametersOptions = {},
): ConfigPanelRemoteParameters => {
  const parameterConfigs = getCurrentToolParameters(nodeType, generationConfig);
  const filterOptions = resolveNodeConfigPanelFilterOptions(parameterFilter, options.keyOrder);

  return getNormalizedParameterConfigs(parameterConfigs, {
    ...options,
    ...filterOptions,
  });
};

/**
 * 从节点 data 恢复生成配置，优先使用 mcpInfo.api_name 确认真实工具。
 */
export const getNodeGenerationConfig = (
  nodeType: TVFlowNodeNextTypeEnum,
  nodeData?: FlowNodeData,
): GenerationConfig | undefined => {
  const mediaType = resolveMediaTypeByNodeType(nodeType);
  if (!mediaType) return undefined;

  const defaultGenerationConfig = getDefaultGenerationConfig(nodeType);
  const model = getModelById(
    mediaType,
    nodeData?.mcpInfo?.model_id ?? defaultGenerationConfig?.model_id,
  );
  const tool = getToolById(
    model,
    mediaType,
    nodeData?.mcpInfo?.node_model_id
      ?? (nodeData as Record<string, any> | undefined)?.tool?.node_model_id
      ?? defaultGenerationConfig?.node_model_id,
    nodeData?.mcpInfo?.api_name ?? defaultGenerationConfig?.api_name,
  );
  const baseConfig = buildGenerationConfig(model, tool) ?? defaultGenerationConfig;
  if (!baseConfig) return undefined;

  return {
    ...baseConfig,
    parameters: {
      ...(baseConfig.parameters ?? {}),
      ...(nodeData?.mcpInfo?.parameters ?? {}),
    },
  };
};

/**
 * 从参数配置中提取默认值，写入节点自身的生成参数。
 * timbre 类型优先取音色素材列表首项，避免被参数配置里的空 default 覆盖。
 */
export const getDefaultParams = (
  parameters?: Record<string, ModelToolParameterItem>,
): Record<string, ParameterValue> => Object.entries(parameters ?? {}).reduce<Record<string, ParameterValue>>(
  (acc, [key, config]) => {
    if (config.type === 'prompt') {
      acc[key] = typeof config.default !== 'undefined' ? config.default : '';
      return acc;
    }

    if (config.type === 'timbre') {
      const { timbreList } = useTeamFlowStore.getState();
      const firstTimbreVoiceId = timbreList[0]?.voice_id;
      acc[key] = config.default ?? firstTimbreVoiceId;
      return acc;
    }

    if (typeof config.default !== 'undefined') {
      acc[key] = config.default;
    }
    return acc;
  },
  {},
);
